import type { PackageStoresOp } from "~/utils/allocationPackageOps"

// Staged package store lists as setPackageStores ops: stage against the saved
// list and read the staged list back.

function mergeStoreSets(earlier: PackageStoresOp, later: PackageStoresOp): PackageStoresOp {
  const add = new Set(earlier.add)
  const remove = new Set(earlier.remove)
  for (const id of later.add) {
    if (remove.has(id)) remove.delete(id)
    else add.add(id)
  }
  for (const id of later.remove) {
    if (add.has(id)) add.delete(id)
    else remove.add(id)
  }
  return { ...earlier, add: [...add], remove: [...remove] }
}

/** Stages assigning or unassigning stores; one op per package, dropped once it nets to nothing. */
export function stagePackageStoreOps(
  ops: PackageStoresOp[],
  packageId: string,
  savedStoreIds: ReadonlySet<string>,
  storeIds: string[],
  assign: boolean,
): PackageStoresOp[] {
  const current = stagedPackageStoreIds(ops, packageId, savedStoreIds)
  const moving = storeIds.filter((id) => current.has(id) !== assign)
  if (moving.length === 0) return ops
  const op: PackageStoresOp = {
    kind: "setPackageStores",
    packageId,
    add: assign ? moving : [],
    remove: assign ? [] : moving,
  }
  const index = ops.findIndex((o) => o.packageId === packageId)
  const merged = index < 0 ? op : mergeStoreSets(ops[index]!, op)
  const rest = index < 0 ? ops : ops.filter((_, i) => i !== index)
  return merged.add.length + merged.remove.length === 0 ? rest : [...rest, merged]
}

/** A package's store list with the staged ops applied. */
export function stagedPackageStoreIds(
  ops: PackageStoresOp[],
  packageId: string,
  savedStoreIds: ReadonlySet<string>,
): Set<string> {
  const ids = new Set(savedStoreIds)
  for (const op of ops) {
    if (op.packageId !== packageId) continue
    for (const id of op.add) ids.add(id)
    for (const id of op.remove) ids.delete(id)
  }
  return ids
}
